import { ProductCategories, ProductCategoriesLink } from "components/Navbar/types"
import PageLoader from "components/PageLoader"
import { Preview } from "components/Preview"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import { useCurrentId } from "hooks/useCurrentId"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { GlassesEffect } from "services/player"
import { clearGlasses, setGlassesOptionsByKey } from "store/product/productSlice"
import { ProductTypeName } from "store/product/types"
import { Cover, IntegrationType, ProductV2 } from "types"
import { downloadFileFromUrl } from "utils/fileReader"
import { getCorrectConfigJson } from "utils/zipEffectFileReader"
import { ProductTranslationsData } from "components/ProductTranslations/ProductTranslations"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Flex,
  Link,
  LinkButton,
  Typography,
} from "@strapi/design-system"
import { ArrowLeft, Check } from "@strapi/icons"
import { UploadGlassesImageCard } from "./UploadGlassesImageCard"
import { GlassesRenderingOptions } from "./GlassesRenderingOptions"
import { useAlert } from "providers/AlertProvider"
import { tintVTOService } from "@/services/tintvto"
import { InfoIcon } from "lucide-react"
import { useProductClientClearCache } from "hooks/query/useProductsQuery"
import { opinionService } from "@/features/leaveOpition/LeaveOpinionFeatureService"
import { selectUserInfo } from "store/user/userSlice"
import { useTintVTOAxiosService } from "services/axiosService"

interface GlassesImagesPage {
  add?: boolean
}

const glassesSampleZipFileName = "Glasses_Sample"
export const glassesSamplePath = `/assets/${glassesSampleZipFileName}.zip`

export const GlassesImagesPage = ({ add }: GlassesImagesPage) => {
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const glassesItem = useAppSelector((state) => state.products.glasses)
  const productTypes = useAppSelector((state) => state.products.types)
  const merchantId = useAppSelector((state) => state.user.userInfo.merchantId)
  const productId = useCurrentId()
  const [loading, setLoading] = useState(false)
  const [loadingGlassesCovers, setLoadingGlassesCovers] = useState(false)
  const [loadRenderingOptions, setLoadRenderingOptions] = useState(false)
  const clearCache = useProductClientClearCache()
  const userInfo = useAppSelector(selectUserInfo)
  const axiosService = useTintVTOAxiosService()
  const [translations, setTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [pendingTranslations, setPendingTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })

  // Load translations from session storage
  useEffect(() => {
    const storedTranslations = sessionStorage.getItem("glassesTranslations")
    const storedMode = sessionStorage.getItem("glassesTranslationsMode")
    if (storedTranslations) {
      const parsed = JSON.parse(storedTranslations)
      if (storedMode === "pending") {
        setPendingTranslations(parsed)
      } else {
        setTranslations(parsed)
      }
    }
  }, [])

  const saveTranslationsAfterProductSave = async (
    productId: string,
    translationsData: ProductTranslationsData
  ) => {
    try {
      const savedProduct = await tintVTOService.productByIdV2(productId)

      const skuToSkuIdMap: Record<string, string> = {}
      savedProduct.colors?.forEach((color) => {
        if (color.skuId && color.sku) skuToSkuIdMap[color.sku] = color.skuId
        if (color.skuId) skuToSkuIdMap[color.skuId] = color.skuId
      })

      const updatedSkuNames: Record<string, any[]> = {}
      Object.entries(translationsData.skuNames || {}).forEach(([key, skuTranslations]) => {
        let actualSkuId = skuToSkuIdMap[key]

        if (!actualSkuId) {
          const matchingColor = savedProduct.colors?.find(
            (c) => (c.skuId || c.sku) === key || c.sku === key
          )
          if (matchingColor?.skuId) actualSkuId = matchingColor.skuId
        }

        if (!actualSkuId) actualSkuId = key

        if (actualSkuId && skuTranslations) updatedSkuNames[actualSkuId] = skuTranslations
      })

      const productTranslationsToSave = (translationsData.productName || []).filter(
        (t) => t.name && t.name.trim() && t.code !== "en"
      )
      if (productTranslationsToSave.length > 0)
        await tintVTOService.saveProductTranslation(
          productId,
          glassesItem.merchant || userInfo.merchantId,
          productTranslationsToSave
        )

      // Save SKU translations for all variants that have translations
      const allSkuIds = Object.keys(updatedSkuNames)
      await Promise.all(
        allSkuIds.map((skuId) => {
          const skuTranslations = updatedSkuNames[skuId] || []
          const translationsWithNames = skuTranslations.filter(
            (t) => t.name && t.name.trim() && t.code !== "en"
          )
          return tintVTOService.saveSKUTranslation(
            skuId,
            glassesItem.merchant || userInfo.merchantId,
            translationsWithNames
          )
        })
      )
    } catch (error) {
      console.error("Failed to save translations:", error)
    }
  }

  const handleSaveItem = async () => {
    setLoading(true)
    let error = null
    let savedProductId = null

    try {
      const typeId = productTypes?.find((el) => el.name === ProductTypeName.Glasses).id ?? ""
      const configJson = await getCorrectConfigJson(glassesItem.renderingOptions)
      void opinionService.completeInitialSetup(userInfo, "glasses")
      const responseJson = await tintVTOService.saveGlassesV2(
        { ...glassesItem, typeId, merchant: merchantId || glassesItem.merchant },
        configJson
      )
      error = responseJson.errors ? responseJson.errors[0] : false
      // saveProductV2 returns the product ID directly (as a number or string)
      const responseId = responseJson.data?.saveProductV2
      savedProductId =
        (responseId && String(responseId)) ||
        (responseJson.data?.saveProductV2 as any)?.id ||
        glassesItem.id

      if (!error && add && userInfo.integrationType === IntegrationType.TIENDANUBE) {
        const skus = glassesItem.colors?.map((color) => color.sku).filter(Boolean) || []
        if (skus.length > 0 && merchantId)
          await axiosService.updateMerchantSkus(merchantId, skus, [])
      }

      if (!error && savedProductId) {
        const translationsToSave = add ? pendingTranslations : translations
        
        // Check if there are any translations to save (including empty arrays to clear)
        const hasTranslationData =
          (translationsToSave?.productName && translationsToSave.productName.length > 0) ||
          (translationsToSave?.skuNames &&
            Object.keys(translationsToSave.skuNames).length > 0 &&
            Object.values(translationsToSave.skuNames).some((arr) => arr && arr.length > 0))

        if (hasTranslationData)
          await saveTranslationsAfterProductSave(savedProductId, translationsToSave)
      }
    } catch (e) {
      error = JSON.parse(JSON.stringify(e)) ?? false
      if (error?.graphQLErrors?.length) error = error?.graphQLErrors[0]
    }

    showAlert({
      error: !!error,
      text: error ? error.message : `Product "${glassesItem.name}" ${add ? "created" : "updated"}`,
    })
    if (!error) {
      sessionStorage.removeItem("glassesTranslations")
      sessionStorage.removeItem("glassesTranslationsMode")
      dispatch(clearGlasses())
      clearCache()
      navigate(`/glasses`)
    }
    setLoading(false)
  }

  const validateForm = () => {
    if (!glassesItem.coverLeft) return false

    if (!glassesItem.coverFront) return false

    if (!glassesItem.coverRight) return false

    return true
  }

  const selectImageHandler = (data: Cover | null, key: keyof ProductV2) => {
    if (data) dispatch(setGlassesOptionsByKey({ key, value: data }))
    else dispatch(setGlassesOptionsByKey({ key, value: null }))
  }

  const downloadGlassesSampleHandler = async () => {
    await downloadFileFromUrl(glassesSamplePath, "Glasses_Sample.zip")
  }

  useEffect(() => {
    if (
      !loadRenderingOptions &&
      glassesItem.coverFront &&
      glassesItem.coverLeft &&
      glassesItem.coverRight &&
      glassesItem.renderingOptions
    ) {
      GlassesEffect.setGlassesRenderingOptions(glassesItem.renderingOptions)
      setLoadRenderingOptions(true)
    }
  }, [glassesItem.renderingOptions])

  useEffect(() => {
    const fetchGlassesImages = async () => {
      if (!productId) return

      if (!glassesItem.coverLeft || !glassesItem.coverRight || !glassesItem.coverFront)
        try {
          setLoadingGlassesCovers(true)
          const product = await tintVTOService.productByIdV2(productId)

          dispatch(setGlassesOptionsByKey({ key: "coverLeft", value: product.coverLeft || null }))
          dispatch(setGlassesOptionsByKey({ key: "coverRight", value: product.coverRight || null }))
          dispatch(setGlassesOptionsByKey({ key: "coverFront", value: product.coverFront || null }))
          setLoadingGlassesCovers(false)
        } catch (error) {
          console.error("Ошибка загрузки изображений очков:", error)
        }
    }

    fetchGlassesImages()
  }, [productId, dispatch])

  return (
    <Box>
      <BaseHeaderLayout
        title={add ? t("glassesImagesPage.addProduct") : t("glassesImagesPage.editProduct")}
        as={"h1"}
        primaryAction={
          <Flex gap={2}>
            <LinkButton
              variant="secondary"
              onClick={() => {
                dispatch(setGlassesOptionsByKey({ key: "dataFilling", value: true }))
                if (productId) navigate(`/glasses/edit?id=${productId}`)
                else navigate("/glasses/add")
              }}
            >
              {t("glassesImagesPage.backToPrevious")}
            </LinkButton>
            <Button
              startIcon={<Check />}
              onClick={handleSaveItem}
              loading={loading}
              disabled={loading || !validateForm()}
            >
              {t("glassesImagesPage.save")}
            </Button>
          </Flex>
        }
        navigationAction={
          <Link
            startIcon={<ArrowLeft />}
            onClick={() => {
              dispatch(clearGlasses())
              navigate(ProductCategoriesLink.GLASSES, { replace: true })
            }}
          >
            {t("glassesImagesPage.backToProducts")}
          </Link>
        }
      />

      <ContentLayout>
        {glassesItem ? (
          <Box
            style={{
              display: "grid",
              gap: 12,
            }}
          >
            <Box
              padding={8}
              hasRadius
              shadow="tableShadow"
              background="neutral0"
              style={{
                gridRowStart: 1,
                gridColumnStart: 1,
                gridRowEnd: 1,
                gridColumnEnd: 1,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: "26px 32px 50px 32px",
              }}
            >
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Typography variant="beta">{t("glassesImagesPage.vtoModelTitle")}</Typography>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <InfoIcon
                    style={{ width: "16px", color: "white", height: "16px", cursor: "pointer" }}
                  />
                  <Button variant="tertiary" onClick={downloadGlassesSampleHandler}>
                    {t("glassesImagesPage.downloadSample")}
                  </Button>
                </Box>
              </Box>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 0,
                }}
              >
                <Box
                  background={
                    glassesItem.coverLeft?.fileBase64 && !loadingGlassesCovers ? "#fff" : "#F6F6F9"
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px 0px 12px 12px",
                  }}
                >
                  <UploadGlassesImageCard
                    isLoading={loadingGlassesCovers}
                    isLeft={true}
                    file={glassesItem.coverLeft}
                    onChange={(data) => selectImageHandler(data, "coverLeft")}
                  />
                </Box>
                <Box
                  background={
                    glassesItem.coverFront?.fileBase64 && !loadingGlassesCovers ? "#fff" : "#F6F6F9"
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px 0px 12px 0px",
                  }}
                >
                  <UploadGlassesImageCard
                    isLoading={loadingGlassesCovers}
                    isMiddle={true}
                    file={glassesItem.coverFront}
                    onChange={(data) => selectImageHandler(data, "coverFront")}
                  />
                </Box>

                <Box
                  background={
                    glassesItem.coverRight?.fileBase64 && !loadingGlassesCovers ? "#fff" : "#F6F6F9"
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px 16px 12px 0px",
                  }}
                >
                  <UploadGlassesImageCard
                    isLoading={loadingGlassesCovers}
                    isRight={true}
                    file={glassesItem.coverRight}
                    onChange={(data) => selectImageHandler(data, "coverRight")}
                  />
                </Box>
              </Box>
            </Box>
            <GlassesRenderingOptions glassesItem={glassesItem} />
            <Box
              padding={8}
              shadow="tableShadow"
              hasRadius
              style={{
                gridRowStart: 1,
                gridColumnStart: 2,
                gridRowEnd: 2,
                gridColumnEnd: 3,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
                maxWidth: "300px",
                padding: "15px 0",
                minWidth: "260px",
              }}
            >
              <Preview
                productItem={glassesItem}
                type={ProductCategories.GLASSES}
                disabled={loadingGlassesCovers}
              />
            </Box>
          </Box>
        ) : (
          <PageLoader />
        )}
      </ContentLayout>
    </Box>
  )
}
